<template>
  <div class="enrollment-list pa-10">
    <title-page>
      {{ $t('onboarding.enrollments') }}
    </title-page>

    <v-row>
      <v-col class="text-right mb-4">
        <v-btn color="primary" small class="mr-2" :to="{ name: 'payment-onboarding.enrollments.new' }">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('onboarding.create-enrollment') }}
        </v-btn>

        <v-btn color="primary" small :to="{ name: 'payment-onboarding.import' }">
          <v-icon left small>mdi-file-upload</v-icon>
          {{ $t('onboarding.import-csv') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-card class="mb-4">
      <v-card-text>
        <v-row class="justify-center align-center">
          <v-col cols="12" md="3">
            <date-picker
                v-model="filters.createdDates"
                :label="$t('created-at')"
                range
                hide-details
                solo
                clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
                v-model="filters.search"
                :label="$t('search')"
                solo
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                :placeholder="$t('onboarding.search-hint')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
                v-model="filters.status"
                :items="statusOptions"
                :label="$t('status')"
                hide-details
                solo
                clearable
            ></v-select>
          </v-col>

          <v-col class="text-right">
            <v-btn small text @click="clearFilters" class="ml-2">
              <v-icon left small>mdi-filter-off</v-icon>
              {{ $t('clear') }}
            </v-btn>
            <v-btn small outlined color="primary" @click="applyFilters">
              <v-icon left small>mdi-magnify</v-icon>
              {{ $t('search') }}
            </v-btn>
          </v-col>

        </v-row>

      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <!-- Active Batch Filter Indicator -->
        <v-alert v-if="filters.batchId" color="purple" dark dense class="mb-4 d-flex align-center">
          <div class="d-flex align-center justify-space-between" style="width: 100%">
            <div class="d-flex align-center">
              <v-icon left>mdi-filter</v-icon>
              <span>{{ $t('onboarding.filtered-by-batch') }}: <strong>{{ filters.batchId }}</strong></span>
            </div>
            <v-btn icon small @click="clearBatchFilter" color="white">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :loading="loading"
          :server-items-length="total"
          :footer-props="{
            'items-per-page-options': [20, 50, 100]
          }"
        >
          <template #item.email="{ item }">
            {{ item.email }}
          </template>

          <template #item.companyName="{ item }">
            <a
              href="#"
              @click.prevent="viewDetails(item)"
              class="text-decoration-none primary--text"
            >
              <span v-if="item.type === 'customer' && item.firstName && item.lastName">
                {{ item.firstName }} {{ item.lastName }}
              </span>
              <span v-else>
                {{ item.companyName || '-' }}
              </span>
            </a>
          </template>

          <template #item.accountNumber="{ item }">
            <div v-if="item.accountNumber">
              <v-icon small left color="blue">mdi-office-building</v-icon>
              {{ item.accountNumber }}
            </div>
            <div v-else-if="item.evcoId">
              <v-icon small left color="purple">mdi-account</v-icon>
              {{ item.evcoId }}
            </div>
            <span v-else>-</span>
          </template>

          <template #item.status="{ item }">
            <div>
              <v-chip small :color="statusColor(item.status)" dark>
                {{ $t(`onboarding.status.${item.status}`) || item.status }}
              </v-chip>
              <v-chip v-if="shouldShowExpiredOrRenew(item)" x-small color="red" dark class="ml-1">
                {{ $t('expired') }}
              </v-chip>
            </div>
          </template>

          <template #item.type="{ item }">
            <div class="d-flex align-center">
              <v-chip small :color="enrollmentTypeColor(item)" :dark="isDarkChip(item)">
                {{ enrollmentTypeLabel(item) }}
              </v-chip>

              <!-- Indicator for customer attached to organization -->
              <v-tooltip bottom v-if="item.attachToOrganization">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="teal"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-1"
                  >mdi-account-group</v-icon>
                </template>
                <span>{{ $t('onboarding.membership') }}: {{ item.attachToOrganization }}</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.createdOrganizationId || item.organizationId">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="goToOrganization(item.createdOrganizationId || item.organizationId)"
                    class="ml-1"
                  >
                    <v-icon color="green" small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('onboarding.view-organization') }}</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.customerId || item.createdCustomerId">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="goToCustomer(item.customerId || item.createdCustomerId)"
                    class="ml-1"
                  >
                    <v-icon color="purple" small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('onboarding.view-customer') }}</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.batchId">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      x-small
                      icon
                      color="purple"
                      v-bind="attrs"
                      v-on="on"
                      @click="filterByBatch(item.batchId)"
                      class="ml-1"
                  >
                    <v-icon small>mdi-file-document-multiple</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('onboarding.filter-by-batch') }}: {{ item.batchId }}</span>
              </v-tooltip>
            </div>
          </template>

          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex justify-end">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="editEnrollment(item)"
                      :disabled="!canEdit(item)"
                    >
                      <v-icon color="primary">mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('edit') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="viewDetails(item)"
                    >
                      <v-icon color="primary">mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('view-details') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="sendEmail(item)"
                      :disabled="!canSendEmail(item) || isTokenExpired(item)"
                    >
                      <v-icon color="primary">mdi-send</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.send-email') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="copyLink(item)"
                      :disabled="!item.token || !item.isTokenValid || isTokenExpired(item)"
                    >
                      <v-icon color="blue">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.copy-link') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="openLink(item)"
                      :disabled="!item.token || !item.isTokenValid || isTokenExpired(item)"
                    >
                      <v-icon color="purple">mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.open-link') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="renewToken(item)"
                      :disabled="!shouldShowExpiredOrRenew(item)"
                      color="orange"
                    >
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.renew-token') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="attachEnrollment(item)"
                      :disabled="!canAttach(item)"
                    >
                      <v-icon color="blue">mdi-link-variant</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.attach-enrollment') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="convert(item)"
                      :disabled="!canConvert(item)"
                    >
                      <v-icon color="green">mdi-check-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ convertLabel(item) }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="cancelEnrollment(item)"
                      :disabled="!canCancel(item)"
                      color="orange"
                    >
                      <v-icon>mdi-cancel</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('onboarding.cancel-enrollment') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      icon
                      v-bind="attrs"
                      v-on="on"
                      @click="deleteEnrollment(item)"
                      :disabled="!canDelete(item)"
                      color="red"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('delete') }}</span>
                </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Detail Dialog -->
    <enrollment-detail-dialog
      v-model="detailDialog"
      :enrollment="selectedEnrollment"
      @send-email="handleSendEmailFromDialog"
      @convert="handleConvertFromDialog"
    />

    <!-- Convert to Organization Dialog -->
    <convert-to-organization-dialog
      v-model="convertDialog"
      :enrollment="enrollmentToConvert"
      @converted="handleConversionSuccess"
    />

    <!-- Attach Enrollment Dialog -->
    <attach-enrollment-dialog
      v-model="attachDialog"
      :enrollment="enrollmentToAttach"
      @attached="handleAttachmentSuccess"
    />

    <!-- Edit Enrollment Dialog -->
    <edit-enrollment-dialog
      v-model="editDialog"
      :enrollment="enrollmentToEdit"
      @updated="handleEnrollmentUpdated"
    />
  </div>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import EnrollmentDetailDialog from './EnrollmentDetailDialog.vue'
import ConvertToOrganizationDialog from './ConvertToOrganizationDialog.vue'
import AttachEnrollmentDialog from './AttachEnrollmentDialog.vue'
import EditEnrollmentDialog from './EditEnrollmentDialog.vue'
import DatePicker from '@/components/blocks/DatePicker.vue'
import CommonMixin from '@/components/mixins/CommonMixin'

export default {
  name: 'EnrollmentList',

  mixins: [CommonMixin],

  components: {
    EnrollmentDetailDialog,
    ConvertToOrganizationDialog,
    AttachEnrollmentDialog,
    EditEnrollmentDialog,
    DatePicker
  },

  data() {
    return {
      items: [],
      loading: false,
      error: null,
      filters: {
        createdDates: [],
        batchId: '',
        search: '',
        status: null
      },
      total: 0,
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
      },
      detailDialog: false,
      selectedEnrollment: null,
      convertDialog: false,
      enrollmentToConvert: null,
      attachDialog: false,
      enrollmentToAttach: null,
      editDialog: false,
      enrollmentToEdit: null
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('email'), value: 'email', sortable: true },
        { text: this.$t('name'), value: 'companyName', sortable: true },
        { text: this.$t('onboarding.account-or-evco'), value: 'accountNumber', sortable: true },
        { text: this.$t('status'), value: 'status', sortable: true },
        { text: this.$t('type'), value: 'type', sortable: false },
        { text: this.$t('created-at'), value: 'createdAt', sortable: true },
        { value: 'actions', sortable: false }
      ]
    },

    statusOptions() {
      return [
        { text: this.$t('all'), value: null },
        { text: this.$t('onboarding.status.draft'), value: 'draft' },
        { text: this.$t('onboarding.status.sent'), value: 'sent' },
        { text: this.$t('onboarding.status.visited'), value: 'visited' },
        { text: this.$t('onboarding.status.validated'), value: 'validated' },
        { text: this.$t('onboarding.status.signed'), value: 'signed' },
        { text: this.$t('onboarding.status.completed'), value: 'completed' },
        { text: this.$t('onboarding.status.converted'), value: 'converted' },
        { text: this.$t('onboarding.status.canceled'), value: 'canceled' },
        { text: this.$t('onboarding.status.expired'), value: 'expired' },
        { text: this.$t('onboarding.status.failed'), value: 'failed' }
      ]
    }
  },

  watch: {
    options: {
      handler() {
        this.loadEnrollments()
      },
      deep: true
    }
  },

  methods: {
    async loadEnrollments() {
      if (!this.options) return

      this.loading = true
      this.error = null

      try {
        const params = {}

        // Pagination
        if (this.options.itemsPerPage > -1) {
          params.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
        }
        params.limit = this.options.itemsPerPage

        // Sorting
        if (this.options.sortBy && this.options.sortBy.length > 0) {
          params.sortBy = this.options.sortBy[0]
        }

        if (this.options.sortDesc && this.options.sortDesc.length > 0) {
          params.sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'
        }

        // Filters
        if (this.filters.createdDates && this.filters.createdDates.length > 0) {
          params.dateFrom = this.filters.createdDates[0]
          if (this.filters.createdDates.length > 1) {
            params.dateTo = this.filters.createdDates[1]
          }
        }
        if (this.filters.batchId) params.batchId = this.filters.batchId
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.status) params.status = this.filters.status

        const data = await PaymentOnboardingRepository.listEnrollments(params)
        this.items = data.items || []
        this.total = data.total || 0
      } catch (err) {
        this.error = err.response?.data?.message || err.message
      } finally {
        this.loading = false
      }
    },

    applyFilters() {
      // Reset to page 1 when applying filters
      if (this.options && this.options.page > 1) {
        this.options.page = 1
      } else {
        this.loadEnrollments()
      }
    },

    clearFilters() {
      this.filters = {
        createdDates: [],
        batchId: '',
        search: '',
        status: null
      }
      this.applyFilters()
    },

    filterByBatch(batchId) {
      this.filters.batchId = batchId
      this.applyFilters()
    },

    clearBatchFilter() {
      this.filters.batchId = ''
      this.applyFilters()
    },

    async viewDetails(item) {
      this.loading = true
      this.error = null

      try {
        // Fetch full enrollment details from dedicated endpoint
        const fullEnrollment = await PaymentOnboardingRepository.getEnrollmentById(item.id)
        this.selectedEnrollment = fullEnrollment
        this.detailDialog = true
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    handleSendEmailFromDialog(item) {
      this.detailDialog = false
      this.sendEmail(item)
    },

    handleConvertFromDialog(item) {
      this.detailDialog = false
      this.convert(item)
    },

    async sendEmail(item) {
      const confirmed = await this.$dialog?.confirm({
        text: this.$t('onboarding.confirm-send-email'),
        title: this.$t('onboarding.send-email')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentOnboardingRepository.sendEnrollmentEmail(item.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.email-sent'))
        this.loadEnrollments()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    copyLink(item) {
      if (!item.token || !item.isTokenValid) return

      const url = `${window.location.origin}/onboarding?token=${item.token}&locale=${item.locale}`

      // Copy to clipboard
      navigator.clipboard.writeText(url).then(() => {
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.link-copied'))
      }).catch(() => {
        // Fallback
        prompt(this.$t('onboarding.copy-link'), url)
      })
    },

    openLink(item) {
      if (!item.token || !item.isTokenValid) return

      const url = `${window.location.origin}/onboarding?token=${item.token}&locale=${item.locale}`
      window.open(url, '_blank')
    },

    convert(item) {
      // Generic convert method that dispatches to the right conversion type
      if (item.type === 'organization') {
        this.convertToOrganization(item)
      } else if (item.type === 'customer') {
        this.convertToCustomer(item)
      }
    },

    convertLabel(item) {
      // Return the appropriate label based on enrollment type
      if (item.type === 'customer') {
        return this.$t('onboarding.convert-to-customer')
      }
      return this.$t('onboarding.convert-to-organization')
    },

    async convertToOrganization(item) {
      if (!this.canConvert(item)) return

      // Fetch full enrollment details before opening dialog
      this.loading = true
      try {
        const fullEnrollment = await PaymentOnboardingRepository.getEnrollmentById(item.id)
        this.enrollmentToConvert = fullEnrollment
        this.convertDialog = true
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    async convertToCustomer(item) {
      if (!this.canConvert(item)) return

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('onboarding.confirm-convert-to-customer'),
        title: this.$t('onboarding.convert-to-customer')
      })

      if (!confirmed) return

      this.loading = true
      try {
        const result = await PaymentOnboardingRepository.convertToCustomer(item.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.customer-created-successfully'))

        // Refresh the list
        this.loadEnrollments()

        // Optionally open customer page
        if (result.customerId) {
          this.$dialog?.confirm({
            text: this.$t('onboarding.conversion-success-customer') + '\n\n' + this.$t('onboarding.view-customer') + '?',
            title: this.$t('onboarding.conversion-success')
          }).then(confirmed => {
            if (confirmed) {
              this.goToCustomer(result.customerId)
            }
          })
        }
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    handleConversionSuccess(result) {
      // Refresh the list to show updated status
      this.loadEnrollments()

      // Optionally open organization in new window after conversion
      if (result.organizationId) {
        this.$dialog?.confirm({
          text: this.$t('onboarding.conversion-success-with-account', { accountNumber: result.accountNumber }) + '\n\n' + this.$t('onboarding.view-organization') + '?',
          title: this.$t('onboarding.conversion-success')
        }).then(confirmed => {
          if (confirmed) {
            this.goToOrganization(result.organizationId)
          }
        })
      }
    },

    canSendEmail(item) {
      // Email sending is allowed for all statuses except terminal ones
      const blockedStatuses = ['completed', 'converted', 'canceled', 'failed', 'expired']
      return !blockedStatuses.includes(item.status)
    },

    canConvert(item) {
      // Enrollment can be converted if:
      // - Status is 'completed' (mandate signed and payment method created)
      // - Has a type ('organization' or 'customer')
      // - Not already converted (no createdOrganizationId, no createdCustomerId, no customerId, no organizationId)
      // Organization prospects → Convert to Organization
      // Customer prospects → Convert to Customer
      return item.status === 'completed' &&
             (item.type === 'organization' || item.type === 'customer') &&
             !item.createdOrganizationId &&
             !item.createdCustomerId &&
             !item.customerId &&
             !item.organizationId
    },

    canAttach(item) {
      // Enrollment can be attached only if:
      // - Has a type (organization or customer prospect) OR no type (generic prospect)
      // - Not already converted/attached
      // Can be done at any time, even after mandate signing
      return (item.type === 'organization' || item.type === 'customer' || !item.type) &&
             !item.createdOrganizationId &&
             !item.createdCustomerId &&
             !item.customerId &&
             !item.organizationId
    },

    async attachEnrollment(item) {
      if (!this.canAttach(item)) return

      // Fetch full enrollment details before opening dialog
      this.loading = true
      try {
        const fullEnrollment = await PaymentOnboardingRepository.getEnrollmentById(item.id)
        this.enrollmentToAttach = fullEnrollment
        this.attachDialog = true
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    handleAttachmentSuccess(result) {
      // Refresh the list to show updated status
      this.loadEnrollments()
    },

    canEdit(item) {
      // Enrollment can only be edited if status is 'draft'
      return item.status === 'draft'
    },

    canDelete(item) {
      // Enrollment can only be deleted if status is 'draft'
      return item.status === 'draft'
    },

    canCancel(item) {
      // Enrollment can be canceled if status is sent, visited, validated, or mandate_previewed
      return ['sent', 'visited', 'validated', 'mandate_previewed'].includes(item.status)
    },

    async editEnrollment(item) {
      if (!this.canEdit(item)) return

      // Fetch full enrollment details before opening dialog
      this.loading = true
      try {
        const fullEnrollment = await PaymentOnboardingRepository.getEnrollmentById(item.id)
        this.enrollmentToEdit = fullEnrollment
        this.editDialog = true
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    handleEnrollmentUpdated(result) {
      // Refresh the list to show updated data
      this.loadEnrollments()
    },

    async deleteEnrollment(item) {
      if (!this.canDelete(item)) return

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('onboarding.confirm-delete-enrollment'),
        title: this.$t('confirm-delete')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentOnboardingRepository.deleteEnrollment(item.id)
        this.notifySuccess('onboarding.enrollment-deleted-successfully')
        this.loadEnrollments()
      } catch (err) {
        const errorCode = err.response?.data?.error
        const errorContext = err.response?.data?.context

        if (errorCode) {
          this.notifyError(this.$t(errorCode, errorContext || {}))
        } else {
          this.notifyError(err)
        }
      } finally {
        this.loading = false
      }
    },

    async cancelEnrollment(item) {
      if (!this.canCancel(item)) return

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('onboarding.confirm-cancel-enrollment'),
        title: this.$t('onboarding.cancel-enrollment')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentOnboardingRepository.cancelEnrollment(item.id)
        this.notifySuccess('onboarding.enrollment-canceled-successfully')
        this.loadEnrollments()
      } catch (err) {
        const errorCode = err.response?.data?.error
        const errorContext = err.response?.data?.context

        if (errorCode) {
          this.notifyError(this.$t(errorCode, errorContext || {}))
        } else {
          this.notifyError(err)
        }
      } finally {
        this.loading = false
      }
    },

    isTokenExpired(item) {
      // Check if isTokenValid is provided by backend
      if (Object.prototype.hasOwnProperty.call(item, 'isTokenValid')) {
        return !item.isTokenValid
      }
      // Fallback: calculate from tokenExpiration
      if (item.tokenExpiration) {
        return new Date(item.tokenExpiration) < new Date()
      }
      return false
    },

    shouldShowExpiredOrRenew(item) {
      // Don't show "Expired" chip or "Renew" button for terminal statuses
      const terminalStatuses = ['completed', 'converted', 'failed']
      return this.isTokenExpired(item) && !terminalStatuses.includes(item.status)
    },

    async renewToken(item) {
      const confirmed = await this.$dialog?.confirm({
        text: this.$t('onboarding.confirm-renew-token')
      })

      if (!confirmed) return

      try {
        this.loading = true
        const result = await PaymentOnboardingRepository.renewEnrollmentToken(item.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('onboarding.token-renewed'))

        // Reload the list to get updated token
        this.loadEnrollments()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    statusColor(status) {
      const colors = {
        draft: 'grey',
        sent: 'blue',
        visited: 'cyan',
        validated: 'teal',
        mandate_previewed: 'indigo',
        signed: 'purple',
        completed: 'green',
        converted: 'success',
        failed: 'red',
        expired: 'orange'
      }
      return colors[status] || 'grey'
    },

    formatDate(val) {
      if (!val) return ''
      try {
        return new Date(val).toLocaleString()
      } catch (e) {
        return String(val)
      }
    },

    goToOrganization(organizationId) {
      if (!organizationId) return
      // Open in new window as requested by user
      const route = this.$router.resolve({
        name: 'organizations.detail',
        params: { id: organizationId }
      })
      window.open(route.href, '_blank')
    },

    goToCustomer(customerId) {
      if (!customerId) return
      // Open in new window
      const route = this.$router.resolve({
        name: 'customer.detail',
        params: { id: customerId }
      })
      window.open(route.href, '_blank')
    },

    enrollmentTypeLabel(item) {
      // Use the explicit 'type' field from backend
      // type = 'organization' → Prospect Organization (can be converted to Organization)
      // type = 'customer' → Prospect Customer (can be converted to Customer)
      // type = null → Generic Prospect

      // Check if already converted/attached
      const isConverted = !!(item.createdOrganizationId || item.organizationId || item.createdCustomerId || item.customerId)

      if (item.type === 'organization') {
        return isConverted ? this.$t('onboarding.organization') : this.$t('onboarding.prospect-organization')
      }

      if (item.type === 'customer') {
        return isConverted ? this.$t('onboarding.customer') : this.$t('onboarding.prospect-customer')
      }

      return this.$t('onboarding.prospect')
    },

    enrollmentTypeColor(item) {
      if (item.type === 'organization') {
        return 'blue'
      }

      if (item.type === 'customer') {
        return 'purple'
      }

      return 'grey'
    },

    isDarkChip(item) {
      // Return true for dark colors (blue, purple) to make text white
      // Return false for light colors (grey) to keep text black
      const color = this.enrollmentTypeColor(item)
      return color === 'blue' || color === 'purple'
    }
  }
}
</script>

<style lang="scss">
.enrollment-list {
  .v-text-field {
    .v-input__slot, .v-input__control {
      min-height: 40px;
    }

  }

}
</style>
